import {
  asArray,
  mapImage,
  mapLink,
  mapText,
  mapTitle,
  mapVideo,
} from "@/components/DesignSystem/utils/teaserHelper"

import { omit } from "lodash"
import {
  handleProductListHeroCTAClick,
  handleProductListHeroImageClick,
} from "@/components/DesignSystem/analytics/productLIstHeroAnalytics"
import { sanitizeTextForAnalytics } from "@/utils/helper"
import { sanitizeText } from "@/js/helperV2"
import { validateProductListHeroProps } from "@/components/DesignSystem/validators/productListHeroValidator"

/**
 * Maps a “Product List Hero” AEM container node (provided via `children`) into KDS props.
 *
 * Reads the first child (`children[0]`) and maps:
 * - `mod` (defaults to `"white"`)
 * - `heading` via `mapTitle(basic.heading)`
 * - `copy` via `mapText(basic.copy)`
 * - `image` via `mapImage(basic.image)`
 * - `video` via `mapVideo(basic.video)`
 * - `CTAs` via `asArray(basic.cta) -> mapLink`
 * - `products` via `content.content`, each with:
 *    - `image` from `p.productImage`
 *    - `title` from `p.title` (uses `mapTitle`, accepts `'p'` by current behavior)
 *    - `href` from `p.href`
 *
 * Returns `null` if `children[0]` is absent. Omits keys whose mapped values are empty/undefined.
 *
 * @param {Object} container Unused container metadata (kept for signature parity).
 * @return {Object|null} Mapped props object or `null` if nothing to map.
 */
export function mapProductListHeroProps(container) {
  try {
    const firstKey =
      container?.[":itemsOrder"]?.[0] ??
      Object.keys(container?.[":items"] ?? {})?.[0]

    const node = firstKey ? container?.[":items"]?.[firstKey] : undefined
    if (!node) return null

    const basic = node.basic
    const content = node.content
    const heading = mapTitle(basic?.heading)
    const copy = mapText(basic?.copy)
    const heroImageSource = basic?.image

    const video = mapVideo(basic?.video)
    const image = mapImage(heroImageSource)
    const mod = basic?.mod || "white"
    const CTAs = asArray(basic?.cta)
      .map(mapLink)
      .filter(Boolean)
      .map(cta => ({
        ...cta,
        onClick: evt => handleProductListHeroCTAClick(evt, cta.text),
      }))

    const productItems = asArray(content?.content)

    const products = productItems
      .map(p => {
        const img = mapImage(omit(p?.productImage?.[0], ["heading", "href"]))
        const title = mapTitle(p?.title?.[0])
        const href = p?.href

        const titleText = sanitizeText(sanitizeTextForAnalytics(p?.title?.text))

        return img && title
          ? {
              image: img,
              title,
              href,
              onClick: () => handleProductListHeroImageClick(titleText),
            }
          : undefined
      })
      .filter(Boolean)

    if (video) {
      video.loop = false
    }

    if (heading) {
      heading.reveal = { enabled: true, delay: 300 }
    }

    if (copy) {
      copy.reveal = { enabled: true, delay: 600 }
    }

    let CTAsReveal

    if (CTAs?.length > 0) {
      CTAsReveal = { enabled: true, delay: 600 }
    }

    let productsReveal

    if (products?.length > 0) {
      productsReveal = { enabled: true, delay: 900 }
    }

    if (image) {
      image.reveal = { enabled: true, delay: 1000 }
    }

    if (video) {
      video.reveal = { enabled: true, delay: 1000 }
    }

    const productListHeroProps = {
      ...(mod ? { mod } : {}),
      ...(heading ? { heading: { ...heading } } : {}),
      ...(image ? { image } : {}),
      ...(video ? { video } : {}),
      ...(copy ? { copy } : {}),
      ...(CTAs.length ? { CTAs } : {}),
      ...(products.length ? { products } : {}),
      ...(CTAsReveal ? { CTAsReveal } : {}),
      ...(productsReveal ? { productsReveal } : {}),
    }

    const { isValid, errors } =
      validateProductListHeroProps(productListHeroProps)

    if (!isValid) {
      console.warn("productListHeroProps validation errors:", errors)
      return null
    }

    return productListHeroProps
  } catch (error) {
    console.error("Error mapping ProductListHero props:", error)
    return null
  }
}
